<template>
    <v-container class="pa-0" fluid>
        <!-- Alerta de Erro -->
        <v-snackbar
            v-model="error.state"
            timeout="5000"
            color="red darken-2 white--text"
            elevation="4"
            top
        >
            <span class="font-weight-medium">{{ error.msg }}</span>
            <template v-slot:action="{ attrs }">
                <v-btn color="white" text v-bind="attrs" @click="error.state = false">
                    Fechar
                </v-btn>
            </template>
        </v-snackbar>

        <!-- Barra de Ações Superior -->
        <v-row class="mb-4 mt-2" align="center">
            <v-col cols="12" sm="5" md="4">
                <v-card outlined class="rounded-lg pa-3 d-flex align-center" color="#f8fafc" style="border-color: #cbd5e1;">
                    <v-checkbox
                        v-model="selectAll"
                        label="Selecionar todos os modelos"
                        color="primary"
                        class="ma-0 pa-0 font-weight-medium"
                        hide-details
                        @change="toggleSelectAll"
                    ></v-checkbox>
                </v-card>
            </v-col>
            <v-col cols="12" sm="7" md="8" class="text-sm-right">
                <v-btn 
                    color="primary"
                    dark
                    large
                    depressed
                    class="rounded-lg px-8 font-weight-bold"
                    @click="generateImages"
                    :loading="loading"
                    :disabled="loading"
                >
                    <v-icon left>mdi-image-multiple</v-icon>
                    Gerar Imagens Selecionadas
                    <template v-slot:loader>
                        <span>A processar...</span>
                        <v-icon right class="custom-loader">mdi-loading</v-icon>
                    </template>
                </v-btn>
            </v-col>
        </v-row>

        <!-- Grelha de Imagens -->
        <v-row align="start" dense class="mt-4">
            <v-col md="3" sm="4" cols="6"
                v-for="(image, i) in images"
                :key="i"
            >
                <v-hover v-slot="{ hover }">
                    <v-card
                        @click="toggleImage(i)"
                        class="pa-2 rounded-lg transition-swing cursor-pointer"
                        :elevation="images[i].selected || hover ? 4 : 1"
                        :style="images[i].selected ? 'border: 2px solid #551ebb;' : 'border: 2px solid transparent;'"
                    >
                        <v-img
                            ref="images"
                            :src="require(`../assets/resources/original/${image.file}`)"
                            :lazy-src="require(`../assets/resources/original/${image.file}`)"
                            contain
                            aspect-ratio="1"
                            class="img grey lighten-4 rounded"
                        >
                            <preview
                                ref="preview"
                                :style="{
                                    'width': image.maskW / (image.height / (refs.images && refs.images[i] && refs.images[i].$el.clientHeight)) + 'px',
                                    'height': image.maskH / (image.height / (refs.images && refs.images[i] && refs.images[i].$el.clientHeight)) + 'px',
                                    'left': image.maskX / (image.height / (refs.images && refs.images[i] && refs.images[i].$el.clientHeight)) + 'px',
                                    'top': image.maskY / (image.height / (refs.images && refs.images[i] && refs.images[i].$el.clientHeight)) + 'px',
                                    'margin-left': ((refs.images && refs.images[i] && refs.images[i].$el.clientWidth) - image.width / (image.height / (refs.images && refs.images[i] && refs.images[i].$el.clientHeight))) / 2 + 'px',
                                }"
                                :class="`mask ${image.circle && 'circle'}`"
                                :image="root.state.preview.image"
                                :coordinates="root.state.preview.coordinates"
                            />
                            <template v-slot:placeholder> 
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                </v-row>
                            </template>
                        </v-img>

                        <v-fade-transition>
                            <v-overlay                                  
                                v-if="(hover && !$isMobile()) || images[i].selected"
                                absolute
                                color="#1e293b"
                                :opacity="images[i].selected ? 0.4 : 0.7"
                                z-index="1"
                            >
                                <v-btn
                                    elevation="2"
                                    color="primary"
                                    icon
                                    fab
                                    large
                                >
                                    <v-icon size="36">{{ images[i].selected ? 'mdi-check-circle' : 'mdi-plus-circle-outline' }}</v-icon>
                                </v-btn>
                            </v-overlay>
                        </v-fade-transition>
                    </v-card>
                </v-hover>
            </v-col>
        </v-row>

        <!-- Botão Flutuante Voltar ao Topo -->
        <back-to-top text="Voltar ao topo" visibleoffset="250">
            <v-btn
                dark
                fab
                bottom
                right
                fixed
                elevation="4"
                color="primary"
                class="mb-6 mr-4"
            >
                <v-icon>mdi-chevron-up</v-icon>
            </v-btn>
        </back-to-top>
    </v-container>
</template>

<script>
    import { Preview } from 'vue-advanced-cropper'
    import streamSaver from 'streamsaver'
    import Jimp from 'jimp/es'
    import JSZip from 'jszip'
    import { data as maskData } from '../assets/resources/maskData.js'

    export default {
        components: {
            Preview
        },
        data() {
            maskData.reverse()
            for(const i in maskData)
                maskData[i].selected = maskData[i].selected || false

            return {
                images: maskData,
                root: this.$root,
                refs: this.$refs,
                selectAll: false,
                error: {
                    state: false,
                    msg: null
                },
                loading: false
            }
        },
        methods: {
            toggleSelectAll(selected) {
                for(const i in this.images)
                    this.images[i].selected = selected
            },
            toggleImage(i) {
                this.images[i].selected = !this.images[i].selected
                if(!this.images[i].selected)
                    this.selectAll = false
            },
            async generateImages() {
                try {
                    if(!this.$root.state.upload.img) {
                        this.error.state = true
                        this.error.msg = 'Envie e recorte o seu logótipo antes de gerar as imagens.'
                        return false
                    }
    
                    const images = this.getSelectedImages();
                    if(!images.length) {
                        this.error.state = true
                        this.error.msg = 'Selecione pelo menos um modelo da grelha.'
                        return false
                    }                       

                    const data = []
                    this.loading = true 
    
                    for(const i in images)
                        data.push(await this.generateImage(images[i], this.$root.state.preview.canvas.toDataURL()))
    
                    this.loading = false

                    if(images.length == 1)
                        return this.openImage(data[0].url, images[0].file)
    
                    const zip = await this.zipFiles(data, images)
                    const zipName = this.$root.state.upload.name.split('.')[0]
    
                    return await this.downloadFile(zip, zipName);
                    
                } catch(error) {
                    console.log(`Error generating images -> ${error}`)
                    this.loading = false
                }
            },
            async zipFiles(data, images) {
                try {
                    const zip = new JSZip()
                    for(const i in data)
                        zip.file(images[i].file, data[i].buffer)
                    const content = await zip.generateAsync({ type: "uint8array" })
                    return content
                } catch(error) {
                    console.log(`Error zipping files -> ${error}`)
                }
            },
            async downloadFile(file, name) {
                try {
                    const fileStream = streamSaver.createWriteStream(`${name}.zip`, {
                        size: file.size
                    })
                    await new Response(file).body.pipeTo(fileStream)
                } catch(error) {
                    console.log(`Error downloading file -> ${error}`)
                }
            },
            async generateImage(mask, logoImg) {
                try {
                    const image = await Jimp.read(require(`@/assets/resources/masks/${mask.file}`))

                    if(mask.mask) {
                        const maskColor = Jimp.rgbaToInt(96, 252, 28, 255);
                        image.scan(0, 0, image.bitmap.width, image.bitmap.height, function(x, y) {
                            if(image.getPixelColor(x, y) == maskColor)
                                image.setPixelColor(0xFFFFFFFF, x, y)
                        });
                            
                        const logo = await Jimp.read(logoImg)
                        logo.cover(mask.maskW, mask.maskH)

                        if(mask.circle)
                            logo.circle()

                        image.composite(logo, mask.maskX, mask.maskY)
                    }

                    const buffer = await image.getBufferAsync(Jimp.MIME_PNG)
                    const blob = new Blob([buffer])
                    const url = URL.createObjectURL(blob, {type: Jimp.MIME_PNG})

                    return { url, buffer }

                } catch(error) {
                    console.log(`Error loading image -> ${error}`)
                }
            },
            async openImage(url, name) {
                const newTab = window.open();
                newTab.document.open()
                newTab.document.write(`
                    <html style="height: 100%; margin: 0px; background: #f8fafc;">
                        <head>
                            <meta name="viewport" content="width=device-width, minimum-scale=0.1">
                            <title>${name}</title>
                        </head>
                        <body style="display: flex">
                            <a href="${url}" download="${name}" style="display: flex; flex: 1"><img style="-webkit-user-select: none;margin: auto;background-color: transparent; max-height: 90%; max-width: 90%; border-radius: 12px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);" src="${url}"></a>
                        </body>
                    </html>
                `)
                newTab.document.close()
            },
            getSelectedImages() {
                const images = [];
                for(const i in this.images)
                    if(this.images[i].selected)
                        images.push(this.images[i])
                return images
            }
        },
    }
</script>

<style>
    .cursor-pointer {
        cursor: pointer;
    }

    .mask {
        position: absolute;
        z-index: 1;
        display: block;
    }

    .mask.circle {
        border-radius: 50%;
    }

    .custom-loader {
        animation: loader 1s infinite linear;
        display: flex;
    }

    @-moz-keyframes loader {
        from { transform: rotate(0); }
        to { transform: rotate(360deg); }
    }
    @-webkit-keyframes loader {
        from { transform: rotate(0); }
        to { transform: rotate(360deg); }
    }
    @-o-keyframes loader {
        from { transform: rotate(0); }
        to { transform: rotate(360deg); }
    }
    @keyframes loader {
        from { transform: rotate(0); }
        to { transform: rotate(360deg); }
    }
</style>